import { z } from 'zod';
import { severitySchema } from './shared/severity';
import { secretConfidenceSchema } from './shared/secret-confidence';
import { agentAutoUpdateConfigSchema } from './agent-auto-update';
// ---------------------------------------------------------------------------
// Setting types
// ---------------------------------------------------------------------------
export const SettingType = {
    Siem: 'siem',
    EnforcementSeverityThreshold: 'enforcement-severity-threshold',
    SecretConfidenceThreshold: 'secret-confidence-threshold',
    DataCollection: 'data-collection',
    InventoryVisibility: 'inventory-visibility',
    AgentAutoUpdate: 'agent-auto-update',
};
export const settingTypeSchema = z.enum([
    SettingType.Siem,
    SettingType.EnforcementSeverityThreshold,
    SettingType.SecretConfidenceThreshold,
    SettingType.DataCollection,
    SettingType.InventoryVisibility,
    SettingType.AgentAutoUpdate,
]);
// ---------------------------------------------------------------------------
// Config status (health of an integration, stored inside the config JSONB)
// ---------------------------------------------------------------------------
export const ConfigStatus = {
    Untested: 'untested',
    Ok: 'ok',
    Error: 'error',
};
export const configStatusSchema = z
    .enum([ConfigStatus.Untested, ConfigStatus.Ok, ConfigStatus.Error])
    .optional();
/** Human-readable messages for each config status. */
export const configStatusMessages = {
    [ConfigStatus.Untested]: 'Integration has not been tested yet.',
    [ConfigStatus.Ok]: 'Integration is connected and working.',
    [ConfigStatus.Error]: 'Integration error: the SIEM endpoint is unreachable or refusing connections. Please verify your configuration.',
};
// ---------------------------------------------------------------------------
// Per-type config schemas
// ---------------------------------------------------------------------------
/*
  SiemProtocol defines the available transport protocols for SIEM forwarding.
  - tcp/udp: raw RFC 5424 syslog over a host:port socket.
  - hec: Splunk HTTP Event Collector — an HTTPS POST to an endpoint URL with a
    bearer token (KIR-3839). Splunk Cloud rejects raw syslog, so HEC is the
    supported path for it.
*/
export var SiemProtocol;
(function (SiemProtocol) {
    SiemProtocol["Tcp"] = "tcp";
    SiemProtocol["Udp"] = "udp";
    SiemProtocol["Hec"] = "hec";
})(SiemProtocol || (SiemProtocol = {}));
/*
  siemProtocolSchema is the Zod schema for validating protocol values.
  Derived from SiemProtocol enum to maintain single source of truth.
*/
export const siemProtocolSchema = z.enum(SiemProtocol);
/**
 * Base SIEM config object (no cross-field refinement). A single flat object
 * (rather than a discriminated union) so existing syslog consumers of
 * `host`/`port` keep compiling. The transport-specific fields are mutually
 * exclusive by protocol (KIR-3839):
 *   - syslog (tcp/udp): `host` + `port`
 *   - hec: `endpointUrl` + `hecToken`
 *
 * `hecToken` is stored encrypted at rest and redacted from GET responses at the
 * handler layer (KIR-4031); the contract only requires a non-empty string.
 *
 * Exported separately from {@link siemConfigSchema} because it is a plain
 * `ZodObject` — callers that need `.partial()` (e.g. lenient form hydration from
 * a possibly-incomplete stored config, where the completeness rules must NOT
 * apply) must use this base, since `.superRefine` returns a schema without
 * `.partial()`.
 */
export const siemConfigBaseSchema = z.object({
    protocol: siemProtocolSchema,
    host: z.string().min(1, 'Host is required').optional(),
    port: z.number().int().min(1).max(65535).optional(),
    endpointUrl: z
        .string()
        .url('A valid HEC endpoint URL is required')
        .optional(),
    hecToken: z.string().min(1, 'HEC token is required').optional(),
    /**
     * Read-only marker the GET response carries in place of the redacted
     * `hecToken` (KIR-4031): `true` when a token is stored. The write path never
     * sends it; the response redactor (`map-response`) always re-derives it, so a
     * stale persisted value cannot leak. Declared here so `.partial()` reads
     * (form hydration) preserve it instead of stripping it.
     */
    hecTokenSet: z.boolean().optional(),
    /**
     * HEC only. When `true`, the backend skips TLS certificate verification when
     * POSTing to the HEC endpoint (KIR-4285). **Insecure** — the HEC bearer token
     * rides that connection, so an unverified endpoint is MITM-exposed. Opt-in per
     * endpoint, default off (absent → secure). Intended for self-signed / on-prem /
     * Splunk-trial endpoints whose default cert cannot be verified. Prefer a
     * trusted cert; a custom-CA-upload alternative is the secure fast-follow.
     */
    insecureSkipVerify: z.boolean().optional(),
    minSeverity: severitySchema,
    /**
     * Forward scan alerts to this destination (KIR-4083). Optional, and **absent
     * means on** — every organization configured before this field existed keeps
     * forwarding alerts exactly as it did. Read it through
     * {@link siemForwardsAlerts} rather than testing the raw field, so the
     * absent-means-true rule lives in one place.
     */
    forwardAlerts: z.boolean().optional(),
    /**
     * Forward audit-log events to this destination (KIR-4083). Optional, and
     * **absent means off** — enabling audit forwarding is an explicit act, so no
     * existing organization starts emitting audit volume on deploy. Read it
     * through {@link siemForwardsAuditLogs}.
     *
     * Unlike alerts, audit events are not filtered by `minSeverity`: an audit row
     * has no application severity, so the threshold has nothing to compare.
     */
    forwardAuditLogs: z.boolean().optional(),
    configStatus: configStatusSchema,
});
/**
 * Full SIEM config validator: the base object plus the cross-field refinement
 * that enforces `host`+`port` for syslog and `endpointUrl`+`hecToken` for HEC.
 * Use this for validation; use {@link siemConfigBaseSchema} for partial reads.
 */
function isHttpsUrl(value) {
    try {
        // The HEC token is a bearer secret sent as an Authorization header; a
        // non-HTTPS endpoint would put it on the wire in cleartext (KIR-3839).
        return new URL(value).protocol === 'https:';
    }
    catch {
        return false;
    }
}
/**
 * The transport-completeness and mutual-exclusivity rules, as a standalone
 * refinement so every surface that accepts a SIEM config runs the *same* checks.
 *
 * Extracted for the public `/api/v1` write contract (KIR-4083), which needs
 * these rules but a different field set (no `configStatus`, no `hecTokenSet`).
 * Re-stating them there would mean a HEC payload could smuggle `host` past one
 * surface and not the other — exactly the hole the mutual-exclusivity checks
 * below were added to close.
 */
export function refineSiemConfig(config, ctx) {
    {
        if (config.protocol === SiemProtocol.Hec) {
            if (!config.endpointUrl) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['endpointUrl'],
                    message: 'HEC endpoint URL is required',
                });
            }
            else if (!isHttpsUrl(config.endpointUrl)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['endpointUrl'],
                    message: 'HEC endpoint URL must use HTTPS',
                });
            }
            if (!config.hecToken) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['hecToken'],
                    message: 'HEC token is required',
                });
            }
            // Mutual exclusivity: a HEC config must not carry syslog fields. Rejecting
            // them at the contract makes the invariant explicit and, together with the
            // field-keyed encrypt/redact in the handler, closes the direct-API path
            // where a mismatched payload could smuggle/leak a token (KIR-4031,
            // GiorgeCh + Bugbot review).
            if (config.host !== undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['host'],
                    message: 'host is not allowed for a HEC config (use endpointUrl)',
                });
            }
            if (config.port !== undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['port'],
                    message: 'port is not allowed for a HEC config (use endpointUrl)',
                });
            }
        }
        else {
            if (!config.host) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['host'],
                    message: 'Host is required',
                });
            }
            if (config.port === undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['port'],
                    message: 'Port is required',
                });
            }
            // Mutual exclusivity: a syslog config must not carry HEC fields — a stray
            // `hecToken` on a tcp/udp payload is what would otherwise be persisted in
            // plaintext and returned on GET to lower-privileged roles (KIR-4031,
            // GiorgeCh + Bugbot review).
            if (config.endpointUrl !== undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['endpointUrl'],
                    message: 'endpointUrl is not allowed for a syslog config',
                });
            }
            if (config.hecToken !== undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['hecToken'],
                    message: 'hecToken is not allowed for a syslog config',
                });
            }
            // `insecureSkipVerify` is HEC-only (the syslog path never reads it); reject
            // it on a syslog config so a direct API call can't persist a misleading
            // "insecure" flag on tcp/udp (KIR-4285, GiorgeCh review).
            if (config.insecureSkipVerify !== undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['insecureSkipVerify'],
                    message: 'insecureSkipVerify is not allowed for a syslog config',
                });
            }
        }
    }
}
export const siemConfigSchema = siemConfigBaseSchema.superRefine(refineSiemConfig);
/**
 * Which streams a SIEM config forwards (KIR-4083).
 *
 * Both toggles are optional with *different* absent-defaults, which is the kind
 * of asymmetry that gets mis-remembered at the third call site. These two
 * helpers are the only place the rule is written:
 *
 * - alerts default **on**, so a config written before the field existed keeps
 *   behaving exactly as it does today;
 * - audit logs default **off**, so nothing starts shipping audit volume to a
 *   customer's collector without someone asking for it.
 */
export function siemForwardsAlerts(config) {
    return config.forwardAlerts !== false;
}
export function siemForwardsAuditLogs(config) {
    return config.forwardAuditLogs === true;
}
/**
 * Sentinel the frontend submits in place of `hecToken` when editing a SIEM
 * config without changing the token (KIR-4031). GET never returns the real
 * token — it returns `hecTokenSet: true` instead — so on edit the UI pre-fills
 * this placeholder; the backend treats it as "keep the stored token" rather than
 * overwriting with it.
 */
export const SIEM_HEC_TOKEN_MASK = '__KIRIN_HEC_TOKEN_UNCHANGED__';
export const enforcementSeverityThresholdConfigSchema = z.object({
    severity: severitySchema,
});
/*
  Secret Alert Confidence Threshold (KIR-3765 follow-up).
  For secret-leakage findings only, alerts whose confidence (derived from the
  Gitleaks entropy) is below the chosen level are hidden from the dashboard,
  don't notify, and aren't blocked in Enforce mode. Default (BE-owned) is 'high'.
*/
export const secretConfidenceThresholdConfigSchema = z.object({
    confidence: secretConfidenceSchema,
});
/*
  Data collection (training opt-out) setting.
  When `storeInDb` is false, Kirin AI is instructed to not persist this
  organization's prompts, agent chats, or file content for training.
  Default is true (opted-in) when no row exists.
*/
export const dataCollectionConfigSchema = z.object({
    storeInDb: z.boolean(),
});
export const DATA_COLLECTION_DEFAULT = {
    storeInDb: true,
};
/*
  Inventory visibility setting.
  Controls how long an inventory entry stays visible after it was last observed
  by Kirin. When an entry has not been seen for `hideAfterDays` days, it is
  hidden from the default inventory view across all tabs (Users, MCPs,
  Extensions, Plugins, AI Instructions). `null` means "Never" — entries are
  never hidden. This is a visibility control only; entries are not deleted and
  reappear if Kirin observes them again.
*/
export const INVENTORY_VISIBILITY_DAY_OPTIONS = [7, 14, 30, 60, 90];
export const inventoryVisibilityConfigSchema = z.object({
    hideAfterDays: z
        .union([
        z.literal(7),
        z.literal(14),
        z.literal(30),
        z.literal(60),
        z.literal(90),
    ])
        .nullable(),
});
/** Default applied when an organization has not configured the setting. */
export const INVENTORY_VISIBILITY_DEFAULT = {
    hideAfterDays: 60,
};
/** Value used for personal accounts, which never hide inventory entries. */
export const INVENTORY_VISIBILITY_ACCOUNT_DEFAULT = {
    hideAfterDays: null,
};
/** Ordered options for the settings dropdown (`null` = "Never"). */
export const INVENTORY_VISIBILITY_OPTIONS = [
    ...INVENTORY_VISIBILITY_DAY_OPTIONS.map(days => ({
        label: `${days} days`,
        value: days,
    })),
    { label: 'Never', value: null },
];
/**
 * Registry mapping each setting type to its config Zod schema.
 * Used by generic handlers to validate the config JSONB per type.
 */
export const settingConfigSchemas = {
    [SettingType.Siem]: siemConfigSchema,
    [SettingType.EnforcementSeverityThreshold]: enforcementSeverityThresholdConfigSchema,
    [SettingType.SecretConfidenceThreshold]: secretConfidenceThresholdConfigSchema,
    [SettingType.DataCollection]: dataCollectionConfigSchema,
    [SettingType.InventoryVisibility]: inventoryVisibilityConfigSchema,
    [SettingType.AgentAutoUpdate]: agentAutoUpdateConfigSchema,
};
// ---------------------------------------------------------------------------
// Generic request / response schemas
// ---------------------------------------------------------------------------
/**
 * Request body for upserting a setting. The `config` object is validated
 * against the per-type schema in `settingConfigSchemas` at the handler level.
 */
export const upsertSettingBodySchema = z.object({
    config: z.record(z.string(), z.unknown()),
});
/**
 * Generic settings response. The `config` field contains the type-specific
 * payload; consumers should parse it with the appropriate schema from
 * `settingConfigSchemas`.
 */
export const settingResponseSchema = z.object({
    id: z.string(),
    settingType: settingTypeSchema,
    /** Set for org-scoped settings; null for user-scoped settings. */
    organizationId: z.string().nullable(),
    enabled: z.boolean(),
    config: z.record(z.string(), z.unknown()),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/**
 * Response for the secret-confidence-threshold resolver endpoint. Extends the
 * generic setting response with the resolved numeric entropy cutoff so the Go
 * agent / IDE extension can decide with a pure `entropy >= minEntropy`
 * comparison and never re-encode the entropy bands. `minEntropy` is `null` when
 * the threshold is `low` (no suppression).
 */
export const secretConfidenceThresholdResponseSchema = settingResponseSchema.extend({
    minEntropy: z.number().nullable(),
});
// ---------------------------------------------------------------------------
// Test connection (PUT ?test=only | true | false)
// ---------------------------------------------------------------------------
/** Query param for PUT: test=only (dry run), true (test then store), false (store only). */
export const upsertSettingTestQuerySchema = z
    .enum(['only', 'true', 'false'])
    .optional()
    .default('false');
/** Response when PUT is called with test=only (connection test result, nothing persisted). */
export const settingTestResultSchema = z.object({
    success: z.boolean(),
    error: z.string().optional(),
});
//# sourceMappingURL=settings.js.map